import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { delay, filter, map, mergeMap, take } from 'rxjs/operators';
import { GTM_CONTAINER_ID, GtmService, WINDOW } from 'wflow-cdk';
import { AuthService } from '../../auth/auth.service';
import { IntercomService } from '../../shared/components/intercom-button/intercom.service';
import { LoadGlobalConst } from '../../store/global/global.actions';
import { LoadOrganizations } from '../../store/organizations/organizations.actions';
import { NavigationInit } from '../../store/router/router.actions';
import {
  LoadGlobalProfile,
  UserIsLoggedIn,
  UserLoadingState,
} from '../../store/user/user.actions';
import { selectUserAccount } from '../../store/user/user.selectors';
import { UserIdentityAPIService } from '../api';
import { SmartlookEventService } from './analytics/analytic-event.service';

@Injectable({
  providedIn: 'root',
})
export class WfOnAppInitService {
  readonly store = inject(Store);
  readonly authSvc = inject(AuthService);
  readonly userSvc = inject(UserIdentityAPIService);
  readonly intercomSvc = inject(IntercomService);
  readonly destroyRef = inject(DestroyRef);
  readonly smartlookEventSvc = inject(SmartlookEventService);
  readonly router = inject(Router);
  readonly gtmSvc = inject(GtmService);
  readonly gtmContainerId = inject(GTM_CONTAINER_ID, { optional: true });
  readonly window = inject(WINDOW);
  readonly document = inject(DOCUMENT);

  static readonly isAppInitialized = false;

  init() {
    this.store.dispatch(NavigationInit());
    if (this.gtmContainerId) {
      this.gtmSvc.load(this.gtmContainerId);
    }
    
    this.loadAccount();
    this.runAnalytic();
    this.runGtmPageTracking();
    this.clearLocalStorageTrash();
    (WfOnAppInitService as { isAppInitialized: boolean }).isAppInitialized =
      true;
  }

  private loadAccount() {
    this.store.dispatch(UserLoadingState({ loaderState: true }));

    this.authSvc.isAuthorize$
      .pipe(
        filter((v) => v),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isAuthorize) => {
        this.store.dispatch(UserIsLoggedIn({ loggedIn: isAuthorize }));
        this.store.dispatch(LoadGlobalProfile());
        this.store.dispatch(UserLoadingState({ loaderState: false }));
        this.store.dispatch(LoadOrganizations());
        this.store.dispatch(LoadGlobalConst());
      });
  }

  private runAnalytic() {
    this.store
      .select(selectUserAccount)
      .pipe(
        filter((account) => !!account),
        take(1),
        mergeMap((account) =>
          combineLatest([
            of(account),
            this.userSvc.getIntercomIdentityVerificationCode(),
          ]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([account, intercomCode]) => {
        this.smartlookEventSvc.init(account);
        this.intercomSvc.init({
          profile: account,
          intercomCode: intercomCode,
        });
      });
  }

  private runGtmPageTracking() {
    this.store
      .select(selectUserAccount)
      .pipe(
        filter((account) => !!account),
        take(1),
        mergeMap((account) =>
          this.router.events.pipe(
            filter(
              (event): event is NavigationEnd => event instanceof NavigationEnd,
            ),
            delay(0),
            map(() => account),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((account) => {
        this.gtmSvc.pushEvent('page_view', {
          // user_id choice: account.id because it is stable (never changes for a user), unique and non-PII (it means it is internal UUID, not email/name)
          user_id: account.id,
          page_location: this.window.location.href,
          page_path: this.window.location.pathname,
          page_title: this.document.title,
        });
      });
  }

  private clearLocalStorageTrash() {
    const url = `${this.window?.location?.href}`;
    if (!url.includes('/callback/')) {
      const arr = Object.entries(this.window?.localStorage ?? {})
        .filter((x) => x[0].includes('oidc.'))
        .map((x) => x[0]);
      for (let a of arr) {
        localStorage.removeItem(a);
      }
    }
  }
}
